/**
 * ActivitySection - секция «Моя активность».
 *
 * Свои действия из журнала audit_log через /api/v2/profile/activity: пользователь определяется сервером по сессии,
 * новые записи сверху, постранично. Действия администратора от вашего имени помечаются (логин администратора
 * сервер не отдает). Для каждой записи - раздел, действие, объект, результат, устройство и IP.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  Chip,
  Tooltip,
  Alert,
  Button,
  IconButton,
  CircularProgress
} from '@mui/material';
import { HistoryOutlined, Refresh, AdminPanelSettingsOutlined } from '@mui/icons-material';
import SectionHeaderDivider from './SectionHeaderDivider';
import { apiErrorMessage } from '../../admin/reports/edit/editApi';
import { fetchActivity, ActivityPage, ActivityRow } from '../profileApi';

const PAGE_SIZES = [10, 25, 50];

const RESULT_STYLE: Record<string, { color: string; bg: string; border: string }> = {
  success: { color: '#047857', bg: '#ecfdf5', border: '#a7f3d0' },
  error: { color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
  denied: { color: '#9a3412', bg: '#fff7ed', border: '#fed7aa' }
};

/** «2026-10-22 14:05:31» -> { date: «22.10.2026», time: «14:05» } */
const splitMoment = (value: string): { date: string; time: string } => {
  const match = value ? value.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/) : null;
  return match ? { date: `${match[3]}.${match[2]}.${match[1]}`, time: `${match[4]}:${match[5]}` } : { date: value || '—', time: '' };
};

/** Объект действия: подпись, аптека и период - что есть */
const objectOf = (row: ActivityRow): string =>
  [row.entityLabel, row.pharmacyNum ? `аптека ${row.pharmacyNum}` : '', row.period].filter(Boolean).join(' · ');

const headCellSx = { fontWeight: 700, color: '#475569', fontSize: '0.78rem', bgcolor: '#f8fafc', whiteSpace: 'nowrap' as const };

// Раскладка - по ширине секции, а не окна: рядом с боковым меню профиля секция заметно уже окна.
// Пять колонок в узкой секции превращаются в прокрутку вбок и высокие строки, поэтому там та же разметка
// показывается карточками: время и результат сверху, ниже действие, раздел и устройство
const STACKED = '@container (max-width: 719px)';

const stackedRowSx = {
  '& td': { borderColor: '#f1f5f9', verticalAlign: 'top', py: 1.25 },
  [STACKED]: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridTemplateAreas: '"when result" "action action" "section section" "device device"',
    columnGap: 2,
    rowGap: 0.5,
    px: 2,
    py: 1.5,
    borderBottom: '1px solid #f1f5f9',
    '&:last-of-type': { borderBottom: 'none' },
    '& td': { border: 'none', p: 0, minWidth: 0, whiteSpace: 'normal' }
  }
} as const;

/** Подписи ячейки в одну строку в карточке: «14:05 22.10.2026», «Chrome IP 10.0.0.1» */
const inlineCellSx = { display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 1 } as const;

const ActivitySection: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [data, setData] = useState<ActivityPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fetchActivity(page, pageSize));
      setLoadError(null);
    } catch (err) {
      setLoadError(apiErrorMessage(err, 'Не удалось загрузить журнал действий'));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = data ? data.rows : [];

  return (
    <Paper
      id="section-activity"
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        borderRadius: '20px',
        bgcolor: '#ffffff',
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.03)',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        scrollMarginTop: '24px',
        containerType: 'inline-size'
      }}
    >
      <SectionHeaderDivider
        id="header-activity"
        icon={<HistoryOutlined sx={{ fontSize: 22 }} />}
        title="Моя активность"
        subtitle="Ваши действия в портале по журналу: что, когда и с какого устройства. Незнакомая запись - смените пароль"
        badge={
          <Tooltip title="Обновить" arrow>
            <span>
              <IconButton
                size="small"
                onClick={load}
                disabled={loading}
                aria-label="Обновить журнал действий"
                sx={{ color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '8px', width: 34, height: 34 }}
              >
                {loading ? <CircularProgress size={16} color="inherit" /> : <Refresh fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        }
      />

      {loadError && (
        <Alert
          severity="error"
          sx={{ borderRadius: '12px' }}
          action={
            <Button color="inherit" size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 600 }}>
              Повторить
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}

      {loading && !data && !loadError ? (
        <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      ) : data && rows.length === 0 ? (
        <Typography variant="body2" sx={{ color: '#94a3b8', py: 2 }}>
          В журнале пока нет ваших действий
        </Typography>
      ) : data ? (
        <Box>
          <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: '14px', opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
            <Table size="small" sx={{ [STACKED]: { display: 'block', '& tbody': { display: 'block' } } }}>
              <TableHead sx={{ [STACKED]: { display: 'none' } }}>
                <TableRow>
                  <TableCell sx={headCellSx}>Когда</TableCell>
                  <TableCell sx={headCellSx}>Раздел</TableCell>
                  <TableCell sx={headCellSx}>Действие</TableCell>
                  <TableCell sx={headCellSx}>Результат</TableCell>
                  <TableCell sx={headCellSx}>Устройство</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => {
                  const moment = splitMoment(row.createdAt);
                  const object = objectOf(row);
                  const resultStyle = RESULT_STYLE[row.result] || { color: '#475569', bg: '#f8fafc', border: '#e2e8f0' };
                  const note = row.result === 'success' ? row.summary : row.message || row.summary;
                  return (
                    <TableRow key={row.id} hover sx={stackedRowSx}>
                      <TableCell sx={{ whiteSpace: 'nowrap', [STACKED]: { ...inlineCellSx, gridArea: 'when' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.84rem' }}>
                          {moment.time}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          {moment.date}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ [STACKED]: { ...inlineCellSx, gridArea: 'section' } }}>
                        <Typography variant="body2" sx={{ color: '#0f172a', fontSize: '0.84rem' }}>
                          {row.moduleTitle}
                        </Typography>
                        {row.sectionTitle && (
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            {row.sectionTitle}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 220, [STACKED]: { gridArea: 'action' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ color: '#0f172a', fontSize: '0.84rem', fontWeight: 600 }}>
                            {row.actionTitle}
                          </Typography>
                          {row.byAdministrator && (
                            <Tooltip title="Действие выполнил администратор, вошедший от вашего имени" arrow>
                              <Chip
                                icon={<AdminPanelSettingsOutlined sx={{ fontSize: 14 }} />}
                                label="Администратор"
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: 600, fontSize: '0.68rem', height: 20, borderRadius: '6px', color: '#9a3412', borderColor: '#fed7aa' }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        {object && (
                          <Typography variant="caption" sx={{ color: '#475569', display: 'block' }}>
                            {object}
                          </Typography>
                        )}
                        {note && (
                          <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                            {note}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ [STACKED]: { gridArea: 'result' } }}>
                        <Chip
                          label={row.resultTitle || row.result}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 22,
                            borderRadius: '6px',
                            color: resultStyle.color,
                            bgcolor: resultStyle.bg,
                            border: `1px solid ${resultStyle.border}`
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', [STACKED]: { ...inlineCellSx, gridArea: 'device' } }}>
                        <Typography variant="body2" sx={{ color: '#0f172a', fontSize: '0.84rem' }}>
                          {row.device}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          IP {row.ip || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={data.total}
            page={Math.max(0, data.page - 1)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={PAGE_SIZES}
            onPageChange={(_, next) => setPage(next + 1)}
            onRowsPerPageChange={e => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(1);
            }}
            labelRowsPerPage="Строк на странице:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
            // в узкой колонке выбор числа строк и счетчик переносятся, а не прокручиваются вбок
            sx={{
              '& .MuiTablePagination-toolbar': { px: 0, flexWrap: 'wrap', justifyContent: 'flex-end', rowGap: 0.5 },
              '& .MuiTablePagination-spacer': { display: 'none' }
            }}
          />
        </Box>
      ) : null}
    </Paper>
  );
};

export default ActivitySection;
